import React, { useEffect, useState } from 'react';
import * as Routes from '@/modules/routes';
import cn from '@/utils/cn';
import type { SortDirection, SortState } from './types';

export const TH_CLASS = cn(
  'tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-text-gray-600',
  'tw-px-4 tw-py-3',
);

export const TABLE_WRAPPER_CLASS = cn(
  'tw-bg-white tw-border tw-border-solid tw-border-gray-200',
  'tw-rounded-lg tw-overflow-hidden',
);

export const EMPTY_STATE_CLASS = cn(
  'tw-bg-white tw-border tw-border-solid tw-border-gray-200',
  'tw-rounded-lg tw-p-12 tw-text-center tw-text-sm tw-text-gray-500',
);

// Opens the per-session forensics page. Stops propagation so a table row's PIP click doesn't fire.
// The count says whether there is a log to read; sessions from before event logging show 0.
export const ForensicsLink = ({ eventsCount, sessionId }: { eventsCount?: number; sessionId: string }) => (
  <a
    className={cn(
      'tw-text-sm tw-whitespace-nowrap hover:tw-underline',
      eventsCount === 0 ? 'tw-text-gray-400' : 'tw-text-tci-purple',
    )}
    href={Routes.admin_live_session_path(sessionId)}
    rel="noreferrer"
    target="_blank"
    title={eventsCount === 0 ? 'No events logged for this session' : undefined}
    onClick={event => event.stopPropagation()}
  >
    Forensics{eventsCount != null && ` (${eventsCount.toLocaleString()})`}
  </a>
);

export const formatElapsed = (iso: string): string => {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
};

export const formatDuration = (seconds: number | null): string => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
};

export const buildRowClass = ({
  isSelected,
  onClick,
}: { isSelected: boolean; onClick?: () => void }) => cn(
  'tw-border-t tw-border-solid tw-border-gray-100',
  onClick && 'tw-cursor-pointer hover:tw-bg-gray-50',
  isSelected && 'tw-bg-tci-purple/10 tw-opacity-60',
);

export const LoadingSkeleton = () => (
  <div className="tw-space-y-2">
    {Array.from({ length: 4 }, (_, i) => (
      <div key={i} className="tw-h-12 tw-bg-gray-100 tw-rounded tw-animate-pulse" />
    ))}
  </div>
);

interface SortableThProps {
  active: boolean;
  direction: SortDirection;
  label: string;
  sortKey: string;
  onSort: (key: string) => void;
}

const SORT_BUTTON_CLASS = cn(
  'tw-flex tw-items-center tw-gap-1 tw-w-full tw-bg-transparent tw-border-0 tw-p-0',
  'tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-text-gray-600',
  'tw-cursor-pointer hover:tw-text-gray-900',
);

const SortIndicator = ({ active, direction }: { active: boolean; direction: SortDirection }) => (
  <span
    aria-hidden
    className={cn(
      'tw-text-[10px] tw-leading-none tw-transition-opacity',
      active ? 'tw-opacity-100 tw-text-gray-900' : 'tw-opacity-30',
    )}
  >
    {active && direction === 'asc' ? '▲' : '▼'}
  </span>
);

const ariaSortFor = (active: boolean, direction: SortDirection): 'ascending' | 'descending' | 'none' => {
  if (!active) return 'none';
  return direction === 'asc' ? 'ascending' : 'descending';
};

export const SortableTh = ({
  active, direction, label, sortKey, onSort,
}: SortableThProps) => (
  <th aria-sort={ariaSortFor(active, direction)} className={TH_CLASS}>
    <button
      className={SORT_BUTTON_CLASS}
      type="button"
      onClick={() => onSort(sortKey)}
    >
      <span>{label}</span>
      <SortIndicator active={active} direction={direction} />
    </button>
  </th>
);

interface SearchInputProps {
  debounceMs?: number;
  onChange: (value: string) => void;
  placeholder?: string;
  value: string;
}

// AIDEV-NOTE: Controlled-with-debounce input — parent owns the committed value, this owns the
// in-flight one. Resync local state when parent value changes externally (e.g. URL hydrate or
// Clear all).
export const SearchInput = ({
  debounceMs = 300, onChange, placeholder = 'Search…', value,
}: SearchInputProps) => {
  const [local, setLocal] = useState(value);

  useEffect(() => { setLocal(value); }, [value]);

  useEffect(() => {
    if (local === value) return undefined;
    const handle = setTimeout(() => onChange(local), debounceMs);
    return () => clearTimeout(handle);
  }, [local, debounceMs, onChange, value]);

  return (
    <div className="tw-relative tw-flex-1 tw-min-w-[12rem] tw-max-w-md">
      <i
        aria-hidden
        className="fa fa-search tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm"
      />
      <input
        aria-label="Search sessions"
        className={cn(
          'tw-w-full tw-rounded tw-border tw-border-solid tw-border-gray-300 tw-bg-white',
          'tw-py-2 tw-pl-9 tw-pr-9 tw-text-sm tw-text-gray-900 tw-outline-none',
          'focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-200',
        )}
        placeholder={placeholder}
        type="search"
        value={local}
        onChange={e => setLocal(e.target.value)}
      />
      {local && (
        <button
          aria-label="Clear search"
          className={cn(
            'tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400',
            'tw-bg-transparent tw-border-0 tw-p-1 tw-cursor-pointer hover:tw-text-gray-600',
          )}
          type="button"
          onClick={() => setLocal('')}
        >
          <i aria-hidden className="fa fa-times" />
        </button>
      )}
    </div>
  );
};

export const toggleSortDirection = (current: SortState, key: string): SortState => {
  if (current.key === key) {
    return { dir: current.dir === 'asc' ? 'desc' : 'asc', key };
  }
  return { dir: 'desc', key };
};
